import { useState, useRef, useCallback, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import ToolPageLayout from '../components/ToolPageLayout';
import { useToolGenerate } from '../hooks/useToolGenerate';
import { pickPhotoFromLibrary } from '@/utils/nativePhoto';
import { track } from '@/utils/tracker';

export default function ClothExtractPage() {
  const { t } = useTranslation();
  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const {
    isGenerating,
    result,
    error,
    doGenerate,
    doRegenerate,
    doCancel,
    clearError,
    pointsCost,
    controls,
  } = useToolGenerate('cloth_extract');

  const handleFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      setSourceImage(loadEvent.target?.result as string);
      track.referenceUploadSuccess({ source: 'tool_cloth_extract_source', count: 1 });
    };
    reader.readAsDataURL(file);
  }, []);

  const handleGenerate = () => {
    if (!sourceImage) return;
    doGenerate(t('cloth_extract_defaultPrompt'), sourceImage);
  };

  return (
    <ToolPageLayout
      title={t('extool_clothExtract')}
      icon="ri-t-shirt-air-fill"
      iconBg="linear-gradient(135deg, #F43F5E, #FB7185)"
      iconColor="#F43F5E"
      description={t('extool_clothExtract_desc')}
      guidance={{
        input: t('cloth_extract_inputRequirement'),
        output: t('cloth_extract_expectedResult'),
        fallback: t('cloth_extract_fallbackTip'),
        beforeHint: t('cloth_extract_beforeHint'),
        afterHint: t('cloth_extract_afterHint'),
      }}
      pointsCost={pointsCost}
      onGenerate={handleGenerate}
      isGenerating={isGenerating}
      canGenerate={!!sourceImage}
      result={result}
      error={error}
      onRegenerate={doRegenerate}
      onCancel={doCancel}
      onClearError={clearError}
      controls={controls}
    >
      <div
        className="rounded-3xl p-4 mb-3"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,107,157,0.12)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-5 h-5 rounded-md flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #F43F5E, #FB7185)' }}
          >
            <i className="ri-t-shirt-line text-white text-xs" />
          </div>
          <span className="text-xs font-semibold text-gray-500">
            {t('cloth_extract_sourceLabel')}
          </span>
          <span className="text-[10px] text-red-400 ml-auto">{t('gen_required')}</span>
        </div>

        {sourceImage ? (
          <div
            className="relative w-full rounded-2xl overflow-hidden"
            style={{ aspectRatio: '1/1', maxHeight: '280px', border: '1px solid rgba(255,107,157,0.15)' }}
          >
            <img src={sourceImage} alt="garment source" className="w-full h-full object-contain" />
            <button
              type="button"
              onClick={() => setSourceImage(null)}
              className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-gray-800/60 text-white cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => pickPhotoFromLibrary(fileRef, setSourceImage)}
            className="w-full rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer active:scale-[0.98] transition-transform"
            style={{
              aspectRatio: '1/1',
              maxHeight: '280px',
              background: 'rgba(244,63,94,0.08)',
              border: '2px dashed rgba(244,63,94,0.25)',
            }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(244,63,94,0.12)' }}
            >
              <i className="ri-t-shirt-air-line text-2xl text-rose-400" />
            </div>
            <p className="text-sm text-gray-500">{t('cloth_extract_sourcePlaceholder')}</p>
            <p className="text-xs text-gray-400">{t('img2img_supportFormat')}</p>
          </button>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </ToolPageLayout>
  );
}
